import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { ArrowLeft, Calendar, MapPin } from "lucide-react";
import Seo from "@/components/Seo";

export default function ProjectDetail() {
  const { id } = useParams();
  const [update, setUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/updates/${id}`)
      .then((r) => setUpdate(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-24" data-testid="project-detail-loading">
        <div className="industrial-card h-96 animate-pulse" />
      </div>
    );
  }

  if (error || !update) {
    return (
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center" data-testid="project-detail-error">
        <div className="eyebrow">Not found</div>
        <h1 className="font-display text-3xl uppercase mt-4">Update not found</h1>
        <Link to="/projects" className="btn-secondary mt-6 inline-flex">
          <ArrowLeft size={16} /> Back to updates
        </Link>
      </div>
    );
  }

  const date = new Date(update.created_at).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <article className="bg-[#0a0a0c]" data-testid="page-project-detail">
      <Seo
        path={`/projects/${update.id}`}
        title={update.title}
        description={update.summary}
        image={update.image_url ? fileUrl(update.image_url) : undefined}
        type="article"
      />
      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-12 pb-6">
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 hover:text-[#e63946]"
          data-testid="project-back-link"
        >
          <ArrowLeft size={14} /> Back to all updates
        </Link>
      </div>
      <div className="max-w-4xl mx-auto px-6 md:px-12 pb-10">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="tag-chip tag-chip-red">{update.category}</span>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
            <Calendar size={11} /> {date}
          </span>
          {update.location && (
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
              <MapPin size={11} /> {update.location}
            </span>
          )}
        </div>
        <h1 className="font-display text-3xl md:text-5xl uppercase tracking-tighter leading-[1.02] mt-5">
          {update.title}
        </h1>
        <p className="text-zinc-300 text-lg mt-6 leading-relaxed">{update.summary}</p>
      </div>

      {update.image_url && (
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="industrial-card overflow-hidden">
            <img
              src={fileUrl(update.image_url)}
              alt={update.title}
              className="w-full h-auto max-h-[600px] object-cover"
            />
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <div className="prose prose-invert max-w-none">
          <p className="text-zinc-300 leading-relaxed whitespace-pre-line text-[16px]">
            {update.content}
          </p>
        </div>
      </div>
    </article>
  );
}
